import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import cpeHistoryService from '../../services/cpeHistoryService';
import type { EducationCategory } from '../../services/cpeHistoryService';
import type {
  SubjectRequirementDto,
  SubjectRequirementScope,
  SubjectRequirementTargetKind,
  UpsertSubjectRequirementDto,
} from '../../services/licenseComplianceService';

interface SubjectRequirementDialogProps {
  open: boolean;
  /** When set, the dialog edits this requirement; otherwise it creates a new one. */
  requirement?: SubjectRequirementDto | null;
  /** Called with the validated payload; the parent performs the API call. */
  onSubmit: (dto: UpsertSubjectRequirementDto) => Promise<void>;
  onClose: () => void;
}

const TARGET_OPTIONS: Array<{ value: SubjectRequirementTargetKind; label: string; helper: string }> = [
  {
    value: 'technical',
    label: 'Technical subjects',
    helper: 'Any credit in a NASBA technical field of study (Accounting, Auditing, Taxes, …)',
  },
  {
    value: 'non_technical',
    label: 'Non-technical subjects',
    helper: 'Any credit in a non-technical field (Personal Development, Communications, …)',
  },
  {
    value: 'category_set',
    label: 'Specific categories',
    helper: 'Credits in the categories you pick below',
  },
  {
    value: 'total',
    label: 'Any subject (total hours)',
    helper:
      'Every credit counts regardless of subject; use for state yearly minimums ' +
      '(e.g. PA requires 20 hours in each year of the period)',
  },
  {
    value: 'manual',
    label: 'Specific course or attestation',
    helper:
      'A requirement you check off yourself (e.g. a board-mandated state ethics course), ' +
      'optionally linking the course that satisfied it',
  },
];

/**
 * Add/edit dialog for a subject requirement: target picker (technical group,
 * non-technical group, or a specific set of education categories), required
 * hours, scope, and an optional display name.
 */
export default function SubjectRequirementDialog({
  open,
  requirement,
  onSubmit,
  onClose,
}: SubjectRequirementDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [targetKind, setTargetKind] = useState<SubjectRequirementTargetKind>('technical');
  const [scope, setScope] = useState<SubjectRequirementScope>('total_period');
  const [hours, setHours] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<EducationCategory[]>([]);
  const [categories, setCategories] = useState<EducationCategory[]>([]);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset form state each time the dialog opens
  useEffect(() => {
    if (!open) return;
    setTargetKind(requirement?.targetKind ?? 'technical');
    setScope(requirement?.scope ?? 'total_period');
    setHours(requirement ? String(requirement.requiredHours) : '');
    setName(requirement?.name ?? '');
    setSubmitError(null);
    setSaving(false);
  }, [open, requirement]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    cpeHistoryService
      .getEducationCategories()
      .then((data) => {
        if (!cancelled) setCategories(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Map an edited requirement's category ids to loaded options once both exist
  useEffect(() => {
    if (!open) return;
    if (!requirement || requirement.targetKind !== 'category_set') {
      setSelectedCategories([]);
      return;
    }
    setSelectedCategories(categories.filter((c) => requirement.categoryIds.includes(c.id)));
  }, [open, requirement, categories]);

  const hoursNum = Number(hours);
  const isManual = targetKind === 'manual';

  const defaultName = useMemo(() => {
    if (targetKind === 'total') return scope === 'per_year' ? 'Yearly minimum' : 'All subjects';
    if (targetKind === 'technical') return 'Technical subjects';
    if (targetKind === 'non_technical') return 'Non-technical subjects';
    if (targetKind === 'manual') return 'e.g. State Board Ethics Course';
    return selectedCategories.length > 0
      ? selectedCategories.map((c) => c.name).join(', ')
      : 'Specific categories';
  }, [targetKind, scope, selectedCategories]);

  const validationError = useMemo(() => {
    if (isManual) {
      return name.trim() ? null : 'A name is required for this requirement.';
    }
    if (!hours) return 'Required hours are needed.';
    // Fractional hours are legal (e.g. 1.5) — never force whole numbers (M5)
    if (!Number.isFinite(hoursNum) || hoursNum <= 0) {
      return 'Required hours must be greater than 0.';
    }
    // Credits increment in tenths — validate on the raw string to avoid
    // IEEE 754 false rejections from float arithmetic.
    if (!/^\d+(\.\d)?$/.test(hours.trim())) {
      return 'Required hours must be in increments of 0.1 (e.g. 1.5).';
    }
    if (targetKind === 'category_set' && selectedCategories.length === 0) {
      return 'Pick at least one category.';
    }
    return null;
  }, [isManual, name, hours, hoursNum, targetKind, selectedCategories]);

  async function handleSubmit() {
    if (validationError) return;
    setSaving(true);
    setSubmitError(null);
    try {
      await onSubmit(
        isManual
          ? {
              name: name.trim(),
              targetKind: 'manual',
              scope: 'total_period',
              requiredHours: 0,
              categoryIds: [],
            }
          : {
              name: name.trim() || null,
              targetKind,
              scope,
              requiredHours: hoursNum,
              categoryIds:
                targetKind === 'category_set' ? selectedCategories.map((c) => c.id) : [],
            }
      );
      onClose();
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'Something went wrong. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{requirement ? 'Edit subject requirement' : 'Add subject requirement'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl>
            <FormLabel id="subject-target-label" sx={{ typography: 'body2', fontWeight: 600 }}>
              Which credits count toward this requirement?
            </FormLabel>
            <RadioGroup
              aria-labelledby="subject-target-label"
              value={targetKind}
              onChange={(e) => setTargetKind(e.target.value as SubjectRequirementTargetKind)}
            >
              {TARGET_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ py: 0.5 }}>
                      <Box sx={{ typography: 'body2' }}>{option.label}</Box>
                      <Box sx={{ typography: 'caption', color: 'text.secondary' }}>
                        {option.helper}
                      </Box>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          {targetKind === 'category_set' && (
            <Autocomplete
              multiple
              options={categories}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedCategories}
              onChange={(_, value) => setSelectedCategories(value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                    variant="outlined"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Categories" placeholder="Search categories" size="small" />
              )}
            />
          )}

          {!isManual && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <TextField
                label="Required hours"
                type="number"
                size="small"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                slotProps={{ htmlInput: { min: 1, step: 1 } }}
              />
              <TextField
                select
                label="Applies to"
                size="small"
                value={scope}
                onChange={(e) => setScope(e.target.value as SubjectRequirementScope)}
              >
                <MenuItem value="total_period">Entire reporting period</MenuItem>
                <MenuItem value="per_year">Each year of the period</MenuItem>
              </TextField>
            </Box>
          )}

          <TextField
            label={isManual ? 'Name' : 'Name (optional)'}
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={defaultName}
            helperText={
              isManual
                ? 'Name the obligation. You can check it off and link the course that satisfied it from the Reporting page.'
                : 'Leave blank to use the suggested name.'
            }
          />

          {validationError && (isManual ? name !== '' : hours !== '') && (
            <Alert severity="warning">{validationError}</Alert>
          )}
          {submitError && <Alert severity="error">{submitError}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !!validationError}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {requirement ? 'Save changes' : 'Add requirement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
